/**
 * timezone.ts
 *
 * Fuso horário do workspace (item QA 28) — utilitários compartilhados para
 * exibir datas/horas (ex.: agendamentos de tarefa) no fuso configurado em
 * vez do fuso do navegador do usuário, que pode divergir do time da operação.
 */

export const FALLBACK_WORKSPACE_TIME_ZONE = "UTC"

/** SQL datetime2 API values without an offset represent UTC, never browser-local time. */
function instantDate(value: string | number | Date): Date {
  if (value instanceof Date) return value
  return new Date(typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(value) ? `${value}Z` : value)
}

/** Lista curada dos fusos IANA relevantes à operação (Brasil + limites do país). */
export const WORKSPACE_TIME_ZONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "America/Noronha", label: "Fernando de Noronha (UTC-2)" },
  { value: "America/Sao_Paulo", label: "Brasília (UTC-3)" },
  { value: "America/Bahia", label: "Salvador (UTC-3)" },
  { value: "America/Belem", label: "Belém (UTC-3)" },
  { value: "America/Fortaleza", label: "Fortaleza (UTC-3)" },
  { value: "America/Recife", label: "Recife (UTC-3)" },
  { value: "America/Araguaina", label: "Araguaína (UTC-3)" },
  { value: "America/Maceio", label: "Maceió (UTC-3)" },
  { value: "America/Campo_Grande", label: "Campo Grande (UTC-4)" },
  { value: "America/Cuiaba", label: "Cuiabá (UTC-4)" },
  { value: "America/Santarem", label: "Santarém (UTC-3)" },
  { value: "America/Porto_Velho", label: "Porto Velho (UTC-4)" },
  { value: "America/Boa_Vista", label: "Boa Vista (UTC-4)" },
  { value: "America/Manaus", label: "Manaus (UTC-4)" },
  { value: "America/Eirunepe", label: "Eirunepé (UTC-5)" },
  { value: "America/Rio_Branco", label: "Rio Branco (UTC-5)" },
]

/** Confirma se o navegador reconhece o identificador IANA antes de usá-lo. */
export function isValidTimeZone(timeZone: string | null | undefined): timeZone is string {
  if (!timeZone) return false
  try {
    Intl.DateTimeFormat(undefined, { timeZone })
    return true
  } catch {
    return false
  }
}

/** Retorna o fuso IANA detectado pelo navegador, com UTC como fallback seguro. */
export function getBrowserTimeZone(): string {
  if (typeof Intl === "undefined") return FALLBACK_WORKSPACE_TIME_ZONE
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return isValidTimeZone(timeZone) ? timeZone : FALLBACK_WORKSPACE_TIME_ZONE
}

/** Resolve o fuso a usar: o do workspace, se válido; senão o do navegador. */
export function resolveTimeZone(timeZone: string | null | undefined): string {
  return isValidTimeZone(timeZone) ? timeZone : getBrowserTimeZone()
}

/**
 * Formata uma data/hora no fuso do workspace. `value` aceita string ISO,
 * epoch ou `Date`. Retorna "—" para entradas ausentes/inválidas.
 */
export function formatInWorkspaceTimeZone(
  value: string | number | Date | null | undefined,
  timeZone: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" },
): string {
  if (value === null || value === undefined || value === "") return "—"
  const date = instantDate(value)
  if (Number.isNaN(date.getTime())) return "—"

  return new Intl.DateTimeFormat("pt-BR", { hourCycle: "h23", ...options, timeZone: resolveTimeZone(timeZone) }).format(date)
}

const formatters = new Map<string, Intl.DateTimeFormat>()
function wallParts(value: Date, zone: string): Record<string, number> {
  let formatter = formatters.get(zone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-GB", { timeZone: zone, calendar: "gregory", numberingSystem: "latn",
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" })
    formatters.set(zone, formatter)
  }
  return Object.fromEntries(formatter.formatToParts(value).filter(part => part.type !== "literal").map(part => [part.type, Number(part.value)]))
}
const pad = (value: number) => String(value).padStart(2, "0")

export function workspaceDateTimeInput(value: string | number | Date, timeZone?: string | null): string {
  const date = instantDate(value)
  if (!Number.isFinite(date.getTime())) return ""
  const p = wallParts(date, resolveTimeZone(timeZone))
  return `${String(p.year).padStart(4, "0")}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`
}

export function workspaceDateInput(value: string | number | Date = new Date(), timeZone?: string | null): string {
  return workspaceDateTimeInput(value, timeZone).slice(0, 10)
}

function utcCalendar(year: number, month: number, day: number, hour = 0, minute = 0, second = 0): Date {
  const date = new Date(0)
  date.setUTCFullYear(year, month - 1, day)
  date.setUTCHours(hour, minute, second, 0)
  return date
}

export function validDateInput(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const [year, month, day] = value.split("-").map(Number)
  const date = utcCalendar(year, month, day)
  return year >= 1 && year <= 9999 && date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month && date.getUTCDate() === day
}

export function addDateInputDays(value: string, days: number): string {
  if (!validDateInput(value)) throw new Error("Data inválida.")
  const [year, month, day] = value.split("-").map(Number)
  const date = utcCalendar(year, month, day + days)
  return `${String(date.getUTCFullYear()).padStart(4, "0")}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

function wallCandidates(value: string, zone: string): number[] {
  if (!validDateInput(value.slice(0, 10)) || !/^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
    throw new Error("Informe uma data válida e um horário entre 00:00 e 23:59.")
  }
  const [year, month, day, hour, minute] = value.split(/[-T:]/).map(Number)
  const wall = utcCalendar(year, month, day, hour, minute).getTime()
  const candidates = new Set<number>()
  // Probe both sides of an offset transition, then round-trip every candidate.
  for (let hours = -36; hours <= 36; hours += 6) {
    const sample = wall + hours * 3_600_000
    const p = wallParts(new Date(sample), zone)
    const offset = utcCalendar(p.year, p.month, p.day, p.hour, p.minute, p.second).getTime() - sample
    const candidate = wall - offset
    if (workspaceDateTimeInput(candidate, zone) === value) candidates.add(candidate)
  }
  return [...candidates].sort((a, b) => a - b)
}

export function workspaceWallTimeToUtc(value: string, timeZone?: string | null): Date {
  const candidates = wallCandidates(value, resolveTimeZone(timeZone))
  if (candidates.length !== 1) throw new Error(candidates.length
    ? "Este horário ocorre duas vezes no fuso selecionado. Escolha um horário sem ambiguidade."
    : "Este horário não existe no fuso selecionado. Escolha outro horário.")
  return new Date(candidates[0])
}

function dayBoundary(value: string, zone: string): number {
  // A civil day can begin after midnight when clocks advance.
  for (let minute = 0; minute <= 180; minute++) {
    const candidates = wallCandidates(`${value}T${pad(Math.floor(minute / 60))}:${pad(minute % 60)}`, zone)
    if (candidates.length) return candidates[0]
  }
  throw new Error("Esta data não existe no fuso selecionado.")
}

export function workspacePeriodBounds(from: string, to: string, timeZone?: string | null): { from: number; to: number } {
  if (!validDateInput(from) || !validDateInput(to) || to < from) throw new Error("Informe um período válido.")
  const zone = resolveTimeZone(timeZone)
  return { from: dayBoundary(from, zone), to: dayBoundary(addDateInputDays(to, 1), zone) }
}

export function workspaceMonth(timeZone?: string | null, now = new Date()): { from: string; to: string } {
  const today = workspaceDateInput(now, timeZone)
  const from = `${today.slice(0, 7)}-01`
  const [year, month] = from.split("-").map(Number)
  const end = utcCalendar(year, month + 1, 0)
  return { from, to: `${String(end.getUTCFullYear()).padStart(4, "0")}-${pad(end.getUTCMonth() + 1)}-${pad(end.getUTCDate())}` }
}
